import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaBus,
  FaArrowLeft,
} from "react-icons/fa";
import { campuses, telHref } from "../data/campusesData";
import "./Campuses.css";
import AdmissionDropdown from "../components/AdmissionDropdown";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay },
});

const CampusDetail = () => {
  const { id } = useParams();
  const campus = campuses.find((c) => c.id === id);

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [id]);

  if (!campus) return <Navigate to="/campuses" replace />;

  return (
    <div className="campuses-page">
      {/* Page Header */}
      <section className="page-header">
        <div className="container">
          <motion.div {...fadeUp()}>
            <h1>{campus.name}</h1>
            <p>{campus.tagline}</p>
          </motion.div>
        </div>
      </section>

      {/* Main Info: image + contact/curricula/highlights */}
      <section className="campus-section section bg-white">
        <div className="container">
          <Link to="/campuses" className="campus-back-link">
            <FaArrowLeft /> All Campuses
          </Link>

          <div
            className="campus-content image-right"
            style={{ marginTop: "2rem" }}
          >
            <motion.div className="campus-details" {...fadeUp(0.1)}>
              <div className="campus-info-card">
                <h3>Contact Information</h3>
                {campus.locationLabel && (
                  <p className="contact-location">{campus.locationLabel}</p>
                )}
                {campus.contacts.map((contact, idx) => (
                  <div className="contact-block" key={idx}>
                    {contact.name && <h4>{contact.name}</h4>}
                    {contact.addressLines && (
                      <div className="info-row">
                        <FaMapMarkerAlt className="info-icon" />
                        <p>
                          {contact.addressLines.map((line, i) => (
                            <React.Fragment key={i}>
                              {i > 0 && <br />}
                              {line}
                            </React.Fragment>
                          ))}
                        </p>
                      </div>
                    )}
                    <div className="info-row">
                      <FaPhone className="info-icon" />
                      <div className="info-links">
                        {contact.phones.map((phone) => (
                          <a key={phone} href={telHref(phone)}>
                            {phone}
                          </a>
                        ))}
                      </div>
                    </div>
                    <div className="info-row">
                      <FaEnvelope className="info-icon" />
                      <div className="info-links">
                        {contact.emails.map((email) => (
                          <a key={email} href={`mailto:${email}`}>
                            {email}
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="campus-info-card">
                <h3>Curricula Offered</h3>
                <div className="curricula-tags">
                  {campus.curricula.map((curr, idx) => (
                    <span key={idx} className="curriculum-tag">
                      {curr}
                    </span>
                  ))}
                </div>
                <p>
                  <strong>Grades:</strong> {campus.grades}
                </p>
                {/* <p><strong>Students:</strong> {campus.students}</p> */}
              </div>

              <div className="campus-cta">
                <AdmissionDropdown label="Apply for Admission" className="btn btn-secondary" />
              </div>
            </motion.div>

            <motion.div className="campus-image" {...fadeUp(0.2)}>
              <img src={campus.image} alt={campus.name} />
              {campus.transport && (
                <div className="image-badge">
                  <FaBus className="badge-icon" />
                  <span>Transport Available</span>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* History Section */}
      {campus.history && (
        <section className="section bg-light">
          <div className="container">
            <motion.div {...fadeUp()}>
              <h2 className="section-title">Our Story</h2>
              {campus.history.split("\n\n").map((paragraph, i) => (
                <p key={i} className="campus-history-text">
                  {paragraph}
                </p>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* Academic Programs */}
      {campus.programs && campus.programs.length > 0 && (
        <section className="section bg-white">
          <div className="container">
            <motion.div {...fadeUp()}>
              <h2 className="section-title">Academic Programmes</h2>
            </motion.div>
            <div className="programs-grid">
              {campus.programs.map((prog, idx) => (
                <motion.div
                  key={`${campus.id}-prog-${idx}`}
                  className="program-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.5, delay: idx * 0.1, ease: "easeOut" }}
                >
                  <div className="program-name">{prog.name}</div>
                  <div className="program-grades">{prog.grades}</div>
                  <p className="program-desc">{prog.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Combined Facilities Section */}
      {campus.facilities && campus.facilities.length > 0 && (
        <section className="section bg-light">
          <div className="container">
            <motion.div {...fadeUp()}>
              <h2 className="section-title">Facilities at {campus.name}</h2>
            </motion.div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "36px",
                marginTop: "2rem",
              }}
            >
              {campus.facilities.map((item, idx) => (
                <motion.div
                  key={`${campus.id}-${idx}`}
                  style={{ display: "flex", flexDirection: "column" }}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.5, delay: (idx % 2) * 0.15, ease: "easeOut" }}
                >
                  <div style={{ overflow: "hidden", borderRadius: "10px" }}>
                    <motion.img
                      src={item.src}
                      alt={item.label}
                      style={{
                        width: "100%",
                        height: "300px",
                        objectFit: "cover",
                        display: "block",
                      }}
                      whileHover={{ scale: 1.04 }}
                      transition={{ duration: 0.4 }}
                    />
                  </div>
                  <strong
                    style={{
                      display: "block",
                      fontSize: "1.2rem",
                      fontWeight: "700",
                      color: "#1a56db",
                      textAlign: "center",
                      marginTop: "14px",
                      marginBottom: "6px",
                    }}
                  >
                    {item.label}
                  </strong>
                  <span
                    style={{
                      display: "block",
                      fontSize: "0.92rem",
                      color: "#6b7280",
                      textAlign: "center",
                      lineHeight: "1.65",
                    }}
                  >
                    {item.desc}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Banner */}
      <section className="section campus-cta-banner">
        <div className="container" style={{ textAlign: "center" }}>
          <motion.div {...fadeUp()}>
            <h2>Registration for 2026–27 is Open</h2>
            <p>
              Join the Sri Vani family and be part of a legacy of excellence
              since 1966.
            </p>
            <AdmissionDropdown label="Enquire Now" className="btn btn-primary" />
          </motion.div>
        </div>
      </section>

      {/* Other Campuses */}
      <section className="section bg-light">
        <div className="container">
          <h2 className="section-title" style={{ marginBottom: "1.5rem" }}>
            Our Other Campuses
          </h2>
          <div className="other-campuses-grid">
            {campuses
              .filter((c) => c.id !== campus.id)
              .map((c, idx) => (
                <motion.div
                  key={`${campus.id}-other-${c.id}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.5, delay: idx * 0.1, ease: "easeOut" }}
                >
                  <Link
                    to={`/campuses/${c.id}`}
                    className="other-campus-card"
                  >
                    <img src={c.image} alt={c.name} />
                    <div className="other-campus-info">
                      <h4>{c.name}</h4>
                      <p>{c.tagline}</p>
                    </div>
                  </Link>
                </motion.div>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CampusDetail;
